import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import {
  cacheLoadingErrorTooltip,
  cacheLoadingGeneralTooltip,
  ResizeHandleState,
} from '@helios/generic-components'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { ClearwaterTransactionsKey } from '@helios/panels/clearwater-transactions'
import { ExternalTransactionsKey } from '@helios/panels/external-transactions'
import {
  RootState,
  selectAllSessionCacheStatus,
  selectIsEnfusionSession,
  SessionCacheStatus,
  setFocusedUIComponent,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ComponentContainer } from 'golden-layout'
import { combineLatest, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface TransactionsPanelState {
  resizeHandleState: ResizeHandleState
}

@Component({
  selector: 'hs-transactions',
  templateUrl: './transactions-panel.component.html',
  styleUrls: ['./transactions-panel.component.scss'],
})
export class TransactionsPanelComponent
  extends BasePanelComponent<TransactionsPanelState>
  implements OnInit, OnDestroy
{
  private readonly _destroyed$ = new Subject<void>()

  /**
   * True when the active session is Enfusion-sourced (`kind === 'ENFN'`). Both children
   * then render a static "not applicable" card instead of waiting on the session
   * cache, so this container's cache-loading tooltip would describe a wait that is no
   * longer happening.
   *
   * @internal for test
   */
  notApplicable = false

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
  ) {
    super(container)
  }

  ngOnInit(): void {
    // one stream so the tooltip is always computed from the current value of BOTH
    // inputs - no hand-rolled caching and no subscription-order to reason about
    combineLatest([
      this.store.select(selectIsEnfusionSession),
      this.store.select(selectAllSessionCacheStatus),
    ])
      .pipe(takeUntil(this._destroyed$))
      .subscribe(([isEnfusionSession, status]) => {
        this.notApplicable = isEnfusionSession
        this.updateTitleTooltip(status.CwTransactions, status.ExtTransactions)
      })
  }

  ngOnDestroy(): void {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  onExternalTransactionsFocus() {
    this.store.dispatch(
      setFocusedUIComponent({
        name: 'External Transactions',
        key: ExternalTransactionsKey,
      }),
    )
  }

  onClearwaterTransactionsFocus() {
    this.store.dispatch(
      setFocusedUIComponent({
        name: 'Clearwater Transactions',
        key: ClearwaterTransactionsKey,
      }),
    )
  }

  onResizeFinished(resizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      resizeHandleState,
    })
  }

  /**@internal for test */
  updateTitleTooltip(
    cwTxStatus: SessionCacheStatus,
    extTxStatus: SessionCacheStatus,
  ) {
    //both children show a static 'not applicable' card - there is no cache wait to warn about
    if (this.notApplicable) {
      this.setTitleTooltip(undefined)
      return
    }

    //if either is error, then error
    if (
      cwTxStatus == SessionCacheStatus.ERROR ||
      extTxStatus == SessionCacheStatus.ERROR
    ) {
      this.setTitleTooltip(cacheLoadingErrorTooltip, 'ERROR')
      return
    }

    //else - if either is loading, then warning
    if (this.isCacheLoading(cwTxStatus) || this.isCacheLoading(extTxStatus)) {
      this.setTitleTooltip(cacheLoadingGeneralTooltip, 'WARNING')
      return
    }

    //clear the tooltip for everything else (both done or unknown)
    this.setTitleTooltip(undefined)
  }

  private isCacheLoading(status: SessionCacheStatus) {
    return (
      status == SessionCacheStatus.STARTED ||
      status == SessionCacheStatus.PARTIAL
    )
  }
}
